import React, { ReactNode, MouseEvent } from 'react';
import styled, { css } from 'styled-components';
// --------------------------------------------------------------------------------
import { String, StringProps } from './String';
import { config, Icons } from '../index';

export interface NotificationProps {
  /* Notification title */
  title?: StringProps;
  /* Notification message | body */
  body?: StringProps;
  /* Notification type */
  type?: 'success' | 'warning' | 'danger' | 'info';
  /* Notification position on screen */
  position?:
    | 'left-top'
    | 'right-top'
    | 'left-bottom'
    | 'right-bottom'
    | 'center-top'
    | 'center-bottom';
  /* Notification color */
  toastColor?: string;
  /* Notification background color */
  backgroundColor?: string;
  /* Notification padding */
  padding?: string;
  /* Notification width */
  width?: string;
  /* Notification max width*/
  maxWidth?: string;
  /* Notification min width*/
  minWidth?: string;
  /* Notification animation */
  animation?: string;
  /* Notification border radius */
  borderRadius?: string;
  /* Notification child components */
  children?: ReactNode;
  /* Notification onClick callBack */
  onClick?: (event?: MouseEvent) => void;
  /* Notification onClose callBack */
  onCloseClick?: (event?: MouseEvent) => void;
}

const NotificationWrapper = styled.div<NotificationProps>`
  position: fixed;
  display: flex;
  box-sizing: border-box;
  transition: 0.8s ease-in-out;
  overflow: hidden;

  animation: ${(props) => props.animation};
  width: ${(props) => props.width};
  max-width: ${(props) => props.maxWidth};
  min-width: ${(props) => props.minWidth};

  ${(props) =>
    props.position === 'left-top' &&
    css`
      top: ${config.spacingSmall};
      left: ${config.spacingSmall};
    `}

  ${(props) =>
    props.position === 'right-top' &&
    css`
      top: ${config.spacingSmall};
      right: ${config.spacingSmall};
    `}

  ${(props) =>
    props.position === 'right-bottom' &&
    css`
      bottom: ${config.spacingSmall};
      right: ${config.spacingSmall};
    `}

  ${(props) =>
    props.position === 'left-bottom' &&
    css`
      bottom: ${config.spacingSmall};
      left: ${config.spacingSmall};
    `}

  ${(props) =>
    props.position === 'center-top' &&
    css`
      top: ${config.spacingSmall};
      left: 50%;
      transform: translateX(-50%);
    `}

  ${(props) =>
    props.position === 'center-bottom' &&
    css`
      bottom: ${config.spacingSmall};
      left: 50%;
      transform: translateX(-50%);
    `}
`;

const NotificationContainer = styled.div<NotificationProps>`
  display: flex;
  align-items: center;
  flex: 1;

  border: 1px solid ${(props) => props.toastColor};
  border-top-right-radius: ${(props) => props.borderRadius};
  border-bottom-right-radius: ${(props) => props.borderRadius};
  background-color: ${(props) => props.backgroundColor};
`;

const ToastMessageWrapper = styled.div<NotificationProps>`
  display: flex;
  flex: 1;
  flex-direction: column;

  padding: ${(props) => props.padding};
`;

const ToastActions = styled.div<NotificationProps>`
  display: flex;
  align-self: start;
  justify-content: center;
  align-items: center;
  margin: ${config.spacingSmall};
  padding: 3px;
  width: ${config.spacingSmall};
  height: ${config.spacingSmall};
  border-radius: 50%;
  font-size: 10px;
  cursor: pointer;

  background-color: ${(props) => props.toastColor};
  color: ${config.white};
`;

const StyledStripe = styled.div<NotificationProps>`
  width: ${config.spacingXMedium};

  background-color: ${(props) => props.toastColor};
  border-top-left-radius: ${(props) => props.borderRadius};
  border-bottom-left-radius: ${(props) => props.borderRadius};
`;

const IconContainer = styled.div<NotificationProps>`
  padding: ${config.spacingXSmall};

  color: ${(props) => props.toastColor};
`;

export const Notification: React.FC<NotificationProps> = ({
  title,
  body,
  type = 'success',
  position = 'center-top',
  toastColor,
  backgroundColor = config.white,
  padding = `${config.spacingXMedium} ${config.spacingLarge} ${config.spacingXMedium} ${config.spacingSmall}`,
  animation,
  width = 'unset',
  maxWidth = '600px',
  minWidth = '300px',
  borderRadius = config.spacingXSmall,
  onClick,
  onCloseClick,
  // additional props
  ...props
}) => {
  // define defaults for props if values are not provided
  if (!toastColor) {
    if (type === 'success') toastColor = config.meadow;
    if (type === 'danger') toastColor = config.cedarChest;
    if (type === 'warning') toastColor = config.orangeYellow;
    if (type === 'info') toastColor = config.skyBlue;
  }
  // --------------------------------------------------------------------------------
  if (!animation) animation = 'toast-in-top 0.7s';

  return (
    <NotificationWrapper
      animation={animation}
      maxWidth={maxWidth}
      minWidth={minWidth}
      width={width}
      position={position}
      // --------------------------------------------------------------------------------
      onClick={onClick}
      // any additional props that override the defaults
      {...props}
    >
      <StyledStripe toastColor={toastColor} borderRadius={borderRadius} />
      <NotificationContainer
        toastColor={toastColor}
        borderRadius={borderRadius}
        backgroundColor={backgroundColor}
      >
        <IconContainer toastColor={toastColor}>
          <Icons icon="settings" />
        </IconContainer>

        <ToastMessageWrapper padding={padding}>
          {title && (
            <String
              string="Success action"
              color={config.darkMuted}
              fontSize={config.fontMedium}
              fontWeight="bold"
              limit={1}
              {...title}
            />
          )}
          {body && (
            <String
              string="Your action is now available"
              color={config.darkMuted}
              fontSize={config.fontXSmall}
              padding={`${config.spacingSmall} 0 0 0`}
              limit={3}
              {...body}
            />
          )}
        </ToastMessageWrapper>
        <ToastActions toastColor={toastColor} onClick={onCloseClick}>
          <div>&#x2715;</div>
        </ToastActions>
      </NotificationContainer>
    </NotificationWrapper>
  );
};
